// components/SearchableMultiSelect.jsx
import React, { useState, useRef, useEffect } from "react";
import { FaSearch, FaChevronDown, FaTimes, FaCheck } from "react-icons/fa";

const SearchableMultiSelect = ({
  value = [], // array of selected values
  onChange,
  options = [],
  placeholder = "Select...",
  disabled = false,
  loading = false,
  optionLabelKey = "name",
  optionValueKey = "id",
  getOptionLabel = null,
  getOptionValue = null,
  className = "",
  searchPlaceholder = "Search...",
  noResultsText = "No results found",
  sortOptions = true, // Sort options alphabetically
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Get label from option
  const getLabel = (option) => {
    if (getOptionLabel) return getOptionLabel(option);
    if (typeof option === "string" || typeof option === "number") return option;
    return (
      option[optionLabelKey] || option.name || option.label || String(option)
    );
  };

  // Get value from option
  const getValue = (option) => {
    if (getOptionValue) return getOptionValue(option);
    if (typeof option === "string" || typeof option === "number") return option;
    return option[optionValueKey] || option.id || option.value || option;
  };

  // Filter and sort options
  const getFilteredAndSortedOptions = () => {
    let filtered = [...options];

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter((option) => {
        const label = getLabel(option).toLowerCase();
        return label.includes(searchTerm.toLowerCase());
      });
    }

    // Sort alphabetically by label
    if (sortOptions) {
      filtered.sort((a, b) => getLabel(a).localeCompare(getLabel(b)));
    }

    return filtered;
  };

  const filteredOptions = getFilteredAndSortedOptions();

  // Get selected labels for display
  const getSelectedLabels = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const selected = options.find((opt) => getValue(opt) === value[0]);
      return selected ? getLabel(selected) : placeholder;
    }
    return `${value.length} selected`;
  };

  // Check if option is selected
  const isSelected = (optionValue) => {
    return value.includes(optionValue);
  };

  // Handle select/deselect
  const handleToggle = (optionValue) => {
    if (isSelected(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  // Clear all selections
  const clearAll = () => {
    onChange([]);
  };

  // Calculate dropdown position
  const calculateDropdownPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 350;

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPosition("top");
      } else {
        setDropdownPosition("bottom");
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      calculateDropdownPosition();
      setTimeout(() => searchInputRef.current.focus(), 100);
    }
  }, [isOpen]);

  const clearSearch = () => {
    setSearchTerm("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <div
      className="searchable-multi-select-wrapper"
      ref={wrapperRef}
      style={{ position: "relative", width: "100%" }}
    >
      <div
        ref={triggerRef}
        className={`searchable-multi-select-trigger ${className} ${disabled ? "disabled" : ""} ${isOpen ? "open" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          padding: "0.25rem 0.5rem",
          fontSize: "0.875rem",
          border: "1px solid #e2e8f0",
          borderRadius: "0.375rem",
          backgroundColor: disabled ? "#f1f5f9" : "white",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "36px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <span
          style={{
            color: value.length > 0 ? "#0f172a" : "#94a3b8",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {loading ? "Loading..." : getSelectedLabels()}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {value.length > 0 && (
            <FaTimes
              style={{
                fontSize: "12px",
                color: "#64748b",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
            />
          )}
          <FaChevronDown
            style={{
              fontSize: "12px",
              color: "#64748b",
              transition: "transform 0.2s",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </div>
      </div>

      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          className="searchable-multi-select-dropdown"
          style={{
            position: "absolute",
            ...(dropdownPosition === "bottom"
              ? { top: "100%", marginTop: "4px" }
              : { bottom: "100%", marginBottom: "4px" }),
            left: 0,
            width: "100%",
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "0.375rem",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
            maxHeight: "350px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Search Input */}
          <div
            style={{
              padding: "8px",
              borderBottom: "1px solid #e2e8f0",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <FaSearch
                style={{
                  position: "absolute",
                  left: "10px",
                  color: "#94a3b8",
                  fontSize: "12px",
                  pointerEvents: "none",
                }}
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                style={{
                  width: "100%",
                  padding: "6px 10px 6px 30px",
                  fontSize: "13px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "0.375rem",
                  outline: "none",
                }}
              />
              {searchTerm && (
                <FaTimes
                  style={{
                    position: "absolute",
                    right: "10px",
                    color: "#94a3b8",
                    fontSize: "12px",
                    cursor: "pointer",
                  }}
                  onClick={clearSearch}
                />
              )}
            </div>
          </div>

          {/* Options List */}
          <div
            style={{
              overflowY: "auto",
              overflowX: "hidden",
              maxHeight: "280px",
              flex: 1,
            }}
          >
            {filteredOptions.length === 0 ? (
              <div
                style={{
                  padding: "12px",
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: "13px",
                }}
              >
                {noResultsText}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = getValue(option);
                const optionLabel = getLabel(option);
                const selected = isSelected(optionValue);

                return (
                  <div
                    key={optionValue || index}
                    onClick={() => handleToggle(optionValue)}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontSize: "13px",
                      backgroundColor: selected ? "#eff6ff" : "transparent",
                      transition: "background-color 0.2s",
                      borderBottom:
                        index !== filteredOptions.length - 1
                          ? "1px solid #f1f5f9"
                          : "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onMouseEnter={(e) => {
                      if (!selected) {
                        e.currentTarget.style.backgroundColor = "#f8fafc";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selected) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "3px",
                        backgroundColor: selected ? "#2563eb" : "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {selected && (
                        <FaCheck style={{ fontSize: "10px", color: "white" }} />
                      )}
                    </div>
                    <span style={{ color: selected ? "#2563eb" : "#0f172a" }}>
                      {optionLabel}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer with selection count */}
          {value.length > 0 && (
            <div
              style={{
                padding: "8px 12px",
                borderTop: "1px solid #e2e8f0",
                fontSize: "12px",
                color: "#64748b",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <span>{value.length} selected</span>
              <button
                onClick={clearAll}
                style={{
                  background: "none",
                  border: "none",
                  color: "#ef4444",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Clear All
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableMultiSelect;
